import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, AlertCircle } from 'lucide-react';
import { useApi } from '../auth/useApi';
import { FormsTab } from './FormsTab';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

interface Me {
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

// -------- Reusable UI --------
const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20, ...style }}>
    {children}
  </div>
);

const Btn: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  disabled?: boolean;
  style?: React.CSSProperties;
}> = ({ children, onClick, variant = 'primary', size = 'md', disabled, style }) => {
  const palette = {
    primary: { bg: C.primary, fg: '#fff', border: C.primary },
    ghost: { bg: 'transparent', fg: C.text, border: C.border },
    danger: { bg: C.danger, fg: '#fff', border: C.danger },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? '#ccc' : palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
      padding: size === 'sm' ? '4px 10px' : '8px 14px', borderRadius: 6,
      fontSize: size === 'sm' ? 12 : 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, ...style,
    }}>{children}</button>
  );
};

const Input: React.FC<any> = (props) => (
  <input {...props} style={{
    border: `1px solid ${C.border}`, padding: '6px 10px', borderRadius: 6, fontSize: 13,
    background: C.bg, color: C.text, ...props.style,
  }} />
);

const Select: React.FC<any> = (props) => (
  <select {...props} style={{
    border: `1px solid ${C.border}`, padding: '6px 10px', borderRadius: 6, fontSize: 13,
    background: C.bg, color: C.text, ...props.style,
  }} />
);

// -------- Tabs --------
const TABS = [
  { key: 'statuses',   label: 'Statuses' },
  { key: 'categories', label: 'Categories' },
  { key: 'sla',        label: 'SLA' },
  { key: 'business',   label: 'Business Hours' },
  { key: 'rules',      label: 'Routing Rules' },
  { key: 'escalation', label: 'Escalation' },
  { key: 'forms',      label: 'Forms' },
  { key: 'departments',label: 'Departments' },
  { key: 'users',      label: 'Users' },
  { key: 'audit',      label: 'Audit Log' },
] as const;

type TabKey = typeof TABS[number]['key'];

export const AdminSettings: React.FC<{ me: Me }> = ({ me }) => {
  const isAdmin = me.role === 'admin';
  const TECH_TABS: TabKey[] = ['forms', 'users'];
  const allowedTabs = isAdmin ? TABS : TABS.filter(t => TECH_TABS.includes(t.key));
  const [tab, setTab] = useState<TabKey>(allowedTabs[0].key);

  if (me.role !== 'admin' && me.role !== 'tech') {
    return <div style={{ padding: 40, color: C.danger }}>
      <AlertCircle size={20} style={{ verticalAlign: 'bottom', marginRight: 6 }} />
      You need admin or tech role to access this page.
    </div>;
  }

  return (
    <div>
      <h1 style={{ color: C.primary, fontSize: 26, margin: '0 0 6px' }}>{isAdmin ? 'Admin Settings' : 'Admin'}</h1>
      <p style={{ color: C.textMuted, margin: '0 0 20px' }}>{isAdmin ? 'Configure statuses, SLAs, routing, escalation, and users.' : 'Manage forms, software, and users.'}</p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}`, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {allowedTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'transparent', border: 'none',
            padding: '10px 16px', cursor: 'pointer', fontSize: 14,
            color: tab === t.key ? C.primary : C.textMuted,
            fontWeight: tab === t.key ? 600 : 500,
            borderBottom: tab === t.key ? `2px solid ${C.primary}` : '2px solid transparent',
            marginBottom: -1, whiteSpace: 'nowrap', flexShrink: 0,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'statuses' && <StatusesTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'sla' && <SLATab />}
      {tab === 'business' && <BusinessHoursTab />}
      {tab === 'rules' && <RoutingRulesTab />}
      {tab === 'escalation' && <EscalationTab />}
      {tab === 'forms' && <FormsTab isAdmin={isAdmin} />}
      {tab === 'departments' && <DepartmentsTab />}
      {tab === 'users' && <UsersTab isAdmin={isAdmin} />}
      {tab === 'audit' && <AuditLogTab />}
    </div>
  );
};

// ============================================================================
// Statuses Tab
// ============================================================================
interface Status {
  StatusId: number;
  Name: string;
  TracksSLA: boolean;
  IsTerminal: boolean;
  SortOrder: number;
  Color: string;
}

const StatusesTab: React.FC = () => {
  const api = useApi();
  const [items, setItems] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#7a9b7e');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<Status[]>('/api/admin/statuses');
      setItems(Array.isArray(data) ? data.sort((a, b) => a.SortOrder - b.SortOrder) : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addStatus = async () => {
    if (!newName.trim()) return;
    try {
      await api.post('/api/admin/statuses', {
        name: newName.trim(),
        color: newColor,
        sortOrder: items.length + 1,
        tracksSLA: true,
        isTerminal: false,
      });
      setNewName('');
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const updateStatus = async (id: number, patch: Partial<Status>) => {
    try {
      const body: any = {};
      if ('Name' in patch) body.name = (patch as any).Name;
      if ('Color' in patch) body.color = (patch as any).Color;
      if ('TracksSLA' in patch) body.tracksSLA = (patch as any).TracksSLA;
      if ('IsTerminal' in patch) body.isTerminal = (patch as any).IsTerminal;
      if ('SortOrder' in patch) body.sortOrder = (patch as any).SortOrder;
      await api.patch(`/api/admin/statuses/${id}`, body);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const deleteStatus = async (id: number, name: string) => {
    if (!window.confirm(`Delete status "${name}"? Existing tickets using it may break.`)) return;
    try {
      await api.del(`/api/admin/statuses/${id}`);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  if (loading) return <Card>Loading…</Card>;
  if (error) return <Card><span style={{ color: C.danger }}>Error: {error}</span></Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        Statuses define the ticket lifecycle. <strong>TracksSLA</strong> = SLA timer runs in this state. <strong>Terminal</strong> = ticket is closed.
      </p>

      {items.map(s => (
        <div key={s.StatusId} style={{
          display: 'grid', gridTemplateColumns: '20px 1fr 100px auto auto auto',
          gap: 10, alignItems: 'center',
          padding: '10px 0', borderBottom: `1px solid ${C.border}`,
        }}>
          <input type="color" value={s.Color}
            onChange={e => updateStatus(s.StatusId, { Color: e.target.value })}
            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', background: 'none' }} />
          <Input value={s.Name} onChange={(e: any) => updateStatus(s.StatusId, { Name: e.target.value })} />
          <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'monospace' }}>order: {s.SortOrder}</span>
          <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={s.TracksSLA}
              onChange={e => updateStatus(s.StatusId, { TracksSLA: e.target.checked })} />
            SLA
          </label>
          <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={s.IsTerminal}
              onChange={e => updateStatus(s.StatusId, { IsTerminal: e.target.checked })} />
            Terminal
          </label>
          <Btn variant="ghost" size="sm" onClick={() => deleteStatus(s.StatusId, s.Name)}>
            <Trash2 size={12} />
          </Btn>
        </div>
      ))}

      <div style={{ marginTop: 18, padding: 14, background: C.bg, borderRadius: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
        <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', background: 'none' }} />
        <Input value={newName} onChange={(e: any) => setNewName(e.target.value)} placeholder="New status name…" style={{ flex: 1 }} />
        <Btn onClick={addStatus} disabled={!newName.trim()}><Plus size={12} /> Add</Btn>
      </div>
    </Card>
  );
};

// ============================================================================
// SLA Tab
// ============================================================================
interface SLAConfig {
  Priority: string;
  ResponseMinutes: number;
  ResolveMinutes: number;
}

const SLATab: React.FC = () => {
  const api = useApi();
  const [items, setItems] = useState<SLAConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<SLAConfig[]>('/api/admin/sla');
      setItems(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = async (priority: string, patch: Partial<SLAConfig>) => {
    setSaving(priority);
    try {
      // Backend wants the FULL array of all 4 rows (camelCase)
      const merged = items.map(s => s.Priority === priority ? { ...s, ...patch } : s);
      const body = merged.map(s => ({
        priority: s.Priority,
        responseMinutes: s.ResponseMinutes,
        resolveMinutes: s.ResolveMinutes,
      }));
      await api.patch('/api/admin/sla', body);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
    finally { setSaving(null); }
  };

  if (loading) return <Card>Loading…</Card>;
  if (error) return <Card><span style={{ color: C.danger }}>Error: {error}</span></Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        Response and resolution targets in minutes, per priority. SLA timers pause when ticket status doesn't track SLA.
      </p>

      <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ background: C.bg }}>
          <tr>
            <th style={th()}>Priority</th>
            <th style={th()}>Response (min)</th>
            <th style={th()}>Response (humanized)</th>
            <th style={th()}>Resolve (min)</th>
            <th style={th()}>Resolve (humanized)</th>
          </tr>
        </thead>
        <tbody>
          {items.map(s => (
            <tr key={s.Priority} style={{ borderTop: `1px solid ${C.border}` }}>
              <td style={td()}><strong>{s.Priority}</strong></td>
              <td style={td()}>
                <input type="number" defaultValue={s.ResponseMinutes}
                  onBlur={e => {
                    const v = parseInt(e.target.value);
                    if (v && v !== s.ResponseMinutes) update(s.Priority, { ResponseMinutes: v });
                  }}
                  style={{ ...inputSty(), width: 90 }} />
                {saving === s.Priority && <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 6 }}>saving…</span>}
              </td>
              <td style={{ ...td(), color: C.textMuted, fontSize: 12 }}>{humanizeMinutes(s.ResponseMinutes)}</td>
              <td style={td()}>
                <input type="number" defaultValue={s.ResolveMinutes}
                  onBlur={e => {
                    const v = parseInt(e.target.value);
                    if (v && v !== s.ResolveMinutes) update(s.Priority, { ResolveMinutes: v });
                  }}
                  style={{ ...inputSty(), width: 90 }} />
              </td>
              <td style={{ ...td(), color: C.textMuted, fontSize: 12 }}>{humanizeMinutes(s.ResolveMinutes)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </Card>
  );
};

const humanizeMinutes = (m: number): string => {
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${(m / 60).toFixed(1)}h`;
  return `${(m / 1440).toFixed(1)}d`;
};

// ============================================================================
// Routing Rules Tab
// ============================================================================
interface RoutingRule {
  RuleId: number;
  Name: string;
  RuleType: 'keyword' | 'user' | 'category';
  KeywordsJson: string | null;   // JSON array stored as string
  CategoryId: number | null;
  AssignToUserId: number | null;
  SecondaryUserIdsJson: string | null;
  Enabled: boolean;
  SortOrder: number;
}

interface User {
  UserId: number;
  DisplayName: string;
  Role: string;
  Email?: string;
  Active?: boolean;
  CanCreateTickets?: boolean;
  DepartmentId?: number | null;
  DepartmentName?: string | null;
}

interface Department {
  DepartmentId: number;
  Name: string;
  SortOrder?: number;
}

interface Category {
  CategoryId: number;
  Name: string;
}

const RoutingRulesTab: React.FC = () => {
  const api = useApi();
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newRuleType, setNewRuleType] = useState<'keyword' | 'category'>('keyword');
  const [newSecondary, setNewSecondary] = useState<number | null>(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [newCategoryId, setNewCategoryId] = useState<number | null>(null);
  const [newAssignee, setNewAssignee] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, u, cats] = await Promise.all([
        api.get<RoutingRule[]>('/api/admin/routing-rules'),
        api.get<User[]>('/api/users'),
        api.get<Category[]>('/api/categories').catch(() => []),
      ]);
      setRules(Array.isArray(r) ? r : []);
      setUsers(Array.isArray(u) ? u.filter(x => x.Role === 'admin' || x.Role === 'tech' || x.Role === 'agent') : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addRule = async () => {
    if (!newName.trim() || !newAssignee) return;
    if (newRuleType === 'keyword' && !newKeyword.trim()) return;
    if (newRuleType === 'category' && !newCategoryId) return;
    try {
      const body: any = {
        name: newName.trim(),
        ruleType: newRuleType,
        assignToUserId: newAssignee,
        enabled: true,
      };
      if (newRuleType === 'keyword') {
        body.keywords = newKeyword.split(',').map(k => k.trim()).filter(Boolean);
      } else {
        body.categoryId = newCategoryId;
      }
      if (newSecondary && newSecondary !== newAssignee) (body as any).secondaryUserIds = [newSecondary];
      await api.post('/api/admin/routing-rules', body);
      setNewName(''); setNewKeyword(''); setNewCategoryId(null); setNewAssignee(null);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const updateRule = async (id: number, patch: Partial<RoutingRule>) => {
    try {
      const body: any = {};
      if ('Name' in patch) body.name = (patch as any).Name;
      if ('Keyword' in patch) body.keywords = (patch as any).Keyword.split(',').map((k: string) => k.trim()).filter(Boolean);
      if ('AssigneeUserId' in patch) body.assignToUserId = (patch as any).AssigneeUserId;
      if ('Enabled' in patch) body.enabled = (patch as any).Enabled;
      // Priority not editable via this endpoint
      await api.patch(`/api/admin/routing-rules/${id}`, body);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const deleteRule = async (id: number, name: string) => {
    if (!window.confirm(`Delete routing rule "${name}"?`)) return;
    try {
      await api.del(`/api/admin/routing-rules/${id}`);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  if (loading) return <Card>Loading…</Card>;
  if (error) return <Card><span style={{ color: C.danger }}>Error: {error}</span></Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        When a new ticket arrives, the first matching keyword rule auto-assigns it.
      </p>

      {rules.length === 0 ? (
        <div style={{ padding: 24, color: C.textMuted, textAlign: 'center', fontSize: 13 }}>
          No routing rules yet.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: C.bg }}>
            <tr>
              <th style={th()}>Name</th>
              <th style={th()}>Type</th>
              <th style={th()}>Condition</th>
              <th style={th()}>Primary</th>
              <th style={th()}>Secondaries</th>
              <th style={th()}>Order</th>
              <th style={th()}>Enabled</th>
              <th style={th()}></th>
            </tr>
          </thead>
          <tbody>
            {rules.map(r => {
              const kwList = (() => {
                try { return JSON.parse(r.KeywordsJson || '[]').join(', '); }
                catch { return r.KeywordsJson || ''; }
              })();
              return (
                <tr key={r.RuleId} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={td()}>
                    <Input defaultValue={r.Name}
                      onBlur={(e: any) => e.target.value !== r.Name && updateRule(r.RuleId, { Name: e.target.value } as any)} />
                  </td>
                  <td style={{ ...td(), color: C.textMuted, fontSize: 12, textTransform: 'capitalize' }}>{r.RuleType}</td>
                  <td style={td()}>
                    {r.RuleType === 'keyword' ? (
                      <Input defaultValue={kwList}
                        onBlur={(e: any) => {
                          const newList = e.target.value.split(',').map((k: string) => k.trim()).filter(Boolean);
                          const oldList = (() => { try { return JSON.parse(r.KeywordsJson || '[]'); } catch { return []; } })();
                          if (JSON.stringify(newList) !== JSON.stringify(oldList)) {
                            updateRule(r.RuleId, { Keyword: e.target.value } as any);
                          }
                        }} />
                    ) : r.RuleType === 'category' ? (
                      <span style={{ fontSize: 13 }}>{categories.find(c => c.CategoryId === r.CategoryId)?.Name ?? '(deleted category)'}</span>
                    ) : (
                      <span style={{ fontSize: 12, color: C.textMuted }}>by submitter</span>
                    )}
                  </td>
                  <td style={td()}>
                    <Select value={r.AssignToUserId ?? ''}
                      onChange={(e: any) => updateRule(r.RuleId, { AssigneeUserId: e.target.value ? parseInt(e.target.value) : null } as any)}>
                      <option value="">— none —</option>
                      {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
                    </Select>
                  </td>
                  <td style={td()}>
                    {(() => {
                      const secs: number[] = (() => {
                        try { return JSON.parse(r.SecondaryUserIdsJson || '[]'); } catch { return []; }
                      })();
                      const setSec = async (slot: number, val: string) => {
                        const next = [...secs];
                        if (val) next[slot] = parseInt(val); else next.splice(slot, 1);
                        const clean = [...new Set(next.filter(Boolean))].filter(uid => uid !== r.AssignToUserId).slice(0, 2);
                        try {
                          await api.patch(`/api/admin/routing-rules/${r.RuleId}`, { secondaryUserIds: clean });
                          setRules(await api.get<RoutingRule[]>('/api/admin/routing-rules'));
                        } catch (e: any) { alert(`Failed to update secondaries: ${e?.message}`); }
                      };
                      return (
                        <div style={{ display: 'grid', gap: 4 }}>
                          {[0, 1].map(slot => (
                            <Select key={slot} value={secs[slot] ?? ''} onChange={(e: any) => setSec(slot, e.target.value)}>
                              <option value="">— none —</option>
                              {users
                                .filter(u => u.UserId !== r.AssignToUserId && (secs[slot] === u.UserId || !secs.includes(u.UserId)))
                                .map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
                            </Select>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ ...td(), color: C.textMuted, fontFamily: 'monospace', fontSize: 12 }}>{r.SortOrder}</td>
                  <td style={td()}>
                    <input type="checkbox" checked={r.Enabled}
                      onChange={e => updateRule(r.RuleId, { Enabled: e.target.checked } as any)} />
                  </td>
                  <td style={td()}>
                    <Btn variant="ghost" size="sm" onClick={() => deleteRule(r.RuleId, r.Name)}>
                      <Trash2 size={12} />
                    </Btn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}

      <p style={{ fontSize: 13, color: C.textMuted, margin: '18px 0 8px' }}>
        Add a rule — <strong>keyword</strong> matches words in the subject/description, <strong>category</strong> matches the ticket's category. The first matching rule assigns the primary; secondaries from every matching rule join as extra workers (max 2).
      </p>
      <div style={{ marginTop: 4, padding: 14, background: C.bg, borderRadius: 6, display: 'grid', gridTemplateColumns: 'minmax(120px,1fr) 130px 1fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
        <Input value={newName} onChange={(e: any) => setNewName(e.target.value)} placeholder="Rule name" />
        <Select value={newRuleType} onChange={(e: any) => setNewRuleType(e.target.value)}>
          <option value="keyword">Keyword</option>
          <option value="category">Category</option>
        </Select>
        {newRuleType === 'keyword' ? (
          <Input value={newKeyword} onChange={(e: any) => setNewKeyword(e.target.value)} placeholder="Keywords (comma-separated)" />
        ) : (
          <Select value={newCategoryId ?? ''} onChange={(e: any) => setNewCategoryId(e.target.value ? parseInt(e.target.value) : null)}>
            <option value="">Pick category…</option>
            {categories.map(c => <option key={c.CategoryId} value={c.CategoryId}>{c.Name}</option>)}
          </Select>
        )}
        <Select value={newAssignee ?? ''} onChange={(e: any) => setNewAssignee(e.target.value ? parseInt(e.target.value) : null)}>
          <option value="">Pick primary…</option>
          {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
        </Select>
        <Select value={newSecondary ?? ''} onChange={(e: any) => setNewSecondary(e.target.value ? parseInt(e.target.value) : null)}>
          <option value="">Secondary (optional)…</option>
          {users.filter(u => u.UserId !== newAssignee).map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
        </Select>
        <Btn onClick={addRule} disabled={!newName.trim() || !newAssignee || (newRuleType === 'keyword' ? !newKeyword.trim() : !newCategoryId)}>
          <Plus size={12} /> Add
        </Btn>
      </div>
    </Card>
  );
};

// ============================================================================
// Escalation Tab
// ============================================================================
interface EscalationChain {
  ChainId: number;
  Name: string;
  IntervalMinutes: number;
  Enabled: boolean;
  TriggerPriority: 'Critical' | 'High' | 'Medium' | 'Low';
}
interface EscalationStep {
  StepId: number;
  UserId: number;
  DisplayName: string;
  Phone: string | null;
  SortOrder: number;
}

const EscalationTab: React.FC = () => {
  const api = useApi();
  const [chain, setChain] = useState<EscalationChain | null>(null);
  const [steps, setSteps] = useState<EscalationStep[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newStepUser, setNewStepUser] = useState<number | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const sendTest = async () => {
    if (!testPhone.trim()) return;
    setTestBusy(true); setTestMsg(null);
    try {
      await api.post('/api/admin/escalation/test', { phone: testPhone.trim() });
      setTestMsg('✓ Test message sent — check the phone.');
    } catch (e: any) {
      setTestMsg(`✗ ${e?.message ?? 'Failed to send'}`);
    } finally { setTestBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [data, allUsers] = await Promise.all([
        api.get<{ chain: EscalationChain; steps: EscalationStep[] }>('/api/admin/escalation'),
        api.get<User[]>('/api/users'),
      ]);
      setChain(data.chain);
      setSteps((data.steps ?? []).sort((a, b) => a.SortOrder - b.SortOrder));
      setUsers(Array.isArray(allUsers) ? allUsers.filter(u => u.Active) : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveChain = async (patch: Partial<EscalationChain>) => {
    setSaving(true);
    try {
      const body: any = {};
      if (patch.Name !== undefined) body.name = patch.Name;
      if (patch.IntervalMinutes !== undefined) body.intervalMinutes = patch.IntervalMinutes;
      if (patch.Enabled !== undefined) body.enabled = patch.Enabled;
      if (patch.TriggerPriority !== undefined) body.triggerPriority = patch.TriggerPriority;
      await api.patch('/api/admin/escalation', body);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
    finally { setSaving(false); }
  };

  const saveSteps = async (newSteps: EscalationStep[]) => {
    setSaving(true);
    try {
      await api.patch('/api/admin/escalation', {
        steps: newSteps.map((s, i) => ({ userId: s.UserId, sortOrder: i + 1 })),
      });
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
    finally { setSaving(false); }
  };

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[idx], next[target]] = [next[target], next[idx]];
    saveSteps(next);
  };

  const remove = (idx: number) => {
    if (!window.confirm(`Remove ${steps[idx].DisplayName} from chain?`)) return;
    saveSteps(steps.filter((_, i) => i !== idx));
  };

  const addStep = () => {
    if (!newStepUser) return;
    const user = users.find(u => u.UserId === newStepUser);
    if (!user) return;
    const newStep: EscalationStep = {
      StepId: 0, UserId: user.UserId, DisplayName: user.DisplayName,
      Phone: null, SortOrder: steps.length + 1,
    };
    setNewStepUser(null);
    saveSteps([...steps, newStep]);
  };

  const savePhone = async (userId: number, phone: string) => {
    setSaving(true);
    try {
      await api.patch(`/api/admin/users/${userId}/phone`, { phone: phone.trim() || null });
      await load();
    } catch (e: any) { alert(`Failed to update phone: ${e?.message}`); }
    finally { setSaving(false); }
  };

  if (loading) return <Card>Loading…</Card>;
  if (error) return <Card><span style={{ color: C.danger }}>Error: {error}</span></Card>;
  if (!chain) return <Card><span style={{ color: C.textMuted }}>No escalation chain configured.</span></Card>;

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Test SMS
        </h3>
        <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 10px' }}>
          Send a one-off text to confirm delivery works, independent of the SLA logic. Use full international format, e.g. <code>+14235551234</code>.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input value={testPhone} onChange={(e: any) => setTestPhone(e.target.value)}
            placeholder="+14235551234" style={{ minWidth: 200 }} />
          <button onClick={sendTest} disabled={testBusy || !testPhone.trim()} style={{
            background: C.primary, color: '#fff', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontSize: 13, cursor: testBusy || !testPhone.trim() ? 'default' : 'pointer',
            opacity: testBusy || !testPhone.trim() ? 0.6 : 1,
          }}>{testBusy ? 'Sending…' : 'Send test'}</button>
          {testMsg && <span style={{ fontSize: 13, color: testMsg.startsWith('✓') ? C.ok : C.danger }}>{testMsg}</span>}
        </div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Chain Settings {saving && <span style={{ fontSize: 11, color: C.textMuted }}>· saving…</span>}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={lblSty()}>Name</label>
            <Input defaultValue={chain.Name}
              onBlur={(e: any) => e.target.value !== chain.Name && saveChain({ Name: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={lblSty()}>Interval (min)</label>
            <Input type="number" defaultValue={chain.IntervalMinutes}
              onBlur={(e: any) => {
                const v = parseInt(e.target.value);
                if (v && v !== chain.IntervalMinutes) saveChain({ IntervalMinutes: v });
              }}
              style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={lblSty()}>Trigger Priority</label>
            <Select value={chain.TriggerPriority}
              onChange={(e: any) => saveChain({ TriggerPriority: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box' }}>
              {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>
          <div>
            <label style={lblSty()}>Enabled</label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 6 }}>
              <input type="checkbox" checked={chain.Enabled}
                onChange={e => saveChain({ Enabled: e.target.checked })} />
              {chain.Enabled ? 'On' : 'Off'}
            </label>
          </div>
        </div>
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Escalation Steps
        </h3>
        <p style={{ fontSize: 12, color: C.textMuted, margin: '0 0 12px' }}>
          When a {chain.TriggerPriority} ticket misses its response SLA, these people are paged via SMS in order, {chain.IntervalMinutes} minutes apart.
        </p>

        {steps.length === 0 ? (
          <div style={{ padding: 20, color: C.textMuted, textAlign: 'center', fontSize: 13 }}>
            No steps configured.
          </div>
        ) : (
          steps.map((s, i) => (
            <div key={s.StepId || `new-${i}`} style={{
              display: 'grid', gridTemplateColumns: '30px 1fr 1fr auto auto auto',
              gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}`,
            }}>
              <span style={{ fontWeight: 700, color: C.primary, fontSize: 14 }}>{i + 1}</span>
              <span>{s.DisplayName}</span>
              <Input
                key={`phone-${s.UserId}-${s.Phone ?? 'null'}`}
                defaultValue={s.Phone ?? ''}
                placeholder="(no phone)"
                onBlur={(e: any) => {
                  const v = e.target.value;
                  if (v !== (s.Phone ?? '')) savePhone(s.UserId, v);
                }}
                style={{ fontSize: 12, fontFamily: 'monospace', padding: '4px 8px' }}
              />
              <Btn variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp size={12} /></Btn>
              <Btn variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === steps.length - 1}><ChevronDown size={12} /></Btn>
              <Btn variant="ghost" size="sm" onClick={() => remove(i)}><Trash2 size={12} /></Btn>
            </div>
          ))
        )}

        <div style={{ marginTop: 16, padding: 14, background: C.bg, borderRadius: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select value={newStepUser ?? ''} onChange={(e: any) => setNewStepUser(e.target.value ? parseInt(e.target.value) : null)} style={{ flex: 1 }}>
            <option value="">Add user to chain…</option>
            {users.filter(u => !steps.some(s => s.UserId === u.UserId)).map(u => (
              <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>
            ))}
          </Select>
          <Btn onClick={addStep} disabled={!newStepUser}><Plus size={12} /> Add Step</Btn>
        </div>
      </Card>
    </>
  );
};

// ============================================================================
// Users Tab
// ============================================================================
const DepartmentSoftwareModal: React.FC<{ dept: Department; onClose: () => void }> = ({ dept, onClose }) => {
  const api = useApi();
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [mapped, setMapped] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [pending, setPending] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [sw, cat] = await Promise.all([
          api.get<{ SoftwareCatalogId: number }[]>(`/api/departments/${dept.DepartmentId}/software`),
          api.get<CatalogRow[]>('/api/users/software-catalog').catch(() => [] as CatalogRow[]),
        ]);
        if (cancelled) return;
        setMapped(new Set((Array.isArray(sw) ? sw : []).map(s => s.SoftwareCatalogId)));
        setCatalog(Array.isArray(cat) ? cat : []);
      } catch (e: any) { if (!cancelled) alert(`Failed to load: ${e?.message}`); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [dept.DepartmentId]); // eslint-disable-line

  // Toggle membership with an optimistic update — no page reload.
  const toggle = async (swId: number) => {
    const isOn = mapped.has(swId);
    setMapped(prev => { const n = new Set(prev); if (isOn) n.delete(swId); else n.add(swId); return n; });
    setPending(prev => { const n = new Set(prev); n.add(swId); return n; });
    try {
      if (isOn) await api.del(`/api/departments/${dept.DepartmentId}/software/${swId}`);
      else      await api.post(`/api/departments/${dept.DepartmentId}/software`, { softwareCatalogId: swId });
    } catch (e: any) {
      setMapped(prev => { const n = new Set(prev); if (isOn) n.add(swId); else n.delete(swId); return n; }); // revert
      alert(`Update failed: ${e?.message ?? e}`);
    } finally {
      setPending(prev => { const n = new Set(prev); n.delete(swId); return n; });
    }
  };

  const q = filter.trim().toLowerCase();
  const shown = q ? catalog.filter(c => c.Name.toLowerCase().includes(q) || (c.Category ?? '').toLowerCase().includes(q)) : catalog;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, color: C.text, borderRadius: 8, padding: 24, width: 'min(560px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>Software for {dept.Name}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: C.textMuted }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 12px' }}>
          Check the software this department typically needs — changes save instantly. Assigning it to individual people is still done on each user.
        </p>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter the catalog…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 4, background: C.surface, color: C.text, marginBottom: 10 }} />

        {loading ? <p style={{ color: C.textMuted, fontSize: 13 }}>Loading…</p>
          : catalog.length === 0 ? <p style={{ color: C.textMuted, fontSize: 13 }}>The software catalog is empty. Add entries under the Software Catalog tab first.</p>
          : (
            <div style={{ overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 6 }}>
              {shown.length === 0 ? <p style={{ color: C.textMuted, fontSize: 13, padding: 12, margin: 0 }}>No matches.</p>
                : shown.map(c => {
                  const checked = mapped.has(c.SoftwareCatalogId);
                  const busy = pending.has(c.SoftwareCatalogId);
                  return (
                    <label key={c.SoftwareCatalogId}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: `1px solid ${C.border}`, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                      <input type="checkbox" checked={checked} disabled={busy} onChange={() => toggle(c.SoftwareCatalogId)} />
                      <span style={{ fontSize: 13 }}><strong>{c.Name}</strong>{c.Category ? <span style={{ color: C.textMuted }}> — {c.Category}</span> : null}</span>
                    </label>
                  );
                })}
            </div>
          )}
        <div style={{ marginTop: 12, fontSize: 12, color: C.textMuted }}>{mapped.size} selected for {dept.Name}</div>
      </div>
    </div>
  );
};

const DepartmentsTab: React.FC = () => {
  const api = useApi();
  const [items, setItems] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [swModalDept, setSwModalDept] = useState<Department | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<Department[]>('/api/departments');
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) { alert(`Failed to load departments: ${e?.message}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post('/api/departments', { name, sortOrder: items.length + 1 });
      setNewName('');
      await load();
    } catch (e: any) { alert(`Add failed: ${e?.message}`); }
    finally { setSaving(false); }
  };

  const rename = async (id: number, oldName: string) => {
    const v = window.prompt('New department name:', oldName);
    if (!v || v.trim() === oldName) return;
    try {
      await api.patch(`/api/departments/${id}`, { name: v.trim() });
      await load();
    } catch (e: any) { alert(`Rename failed: ${e?.message}`); }
  };

  const remove = async (id: number, name: string) => {
    if (!window.confirm(`Delete department "${name}"?\n\nUsers in this department will be left with no department.`)) return;
    try {
      await api.del(`/api/departments/${id}`);
      await load();
    } catch (e: any) { alert(`Delete failed: ${e?.message}`); }
  };

  if (loading) return <Card>Loading…</Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        Departments label your people (set each user's department on the Users tab). You'll
        be able to map the software each department needs here too.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="New department name (e.g. Finance, Marketing, IT)"
          style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 4, background: C.surface }}
        />
        <button onClick={add} disabled={!newName.trim() || saving}
          style={{ background: !newName.trim() ? '#ccc' : C.primary, color: 'white', border: 'none', padding: '8px 18px', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: !newName.trim() ? 'not-allowed' : 'pointer' }}
        >Add department</button>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
          No departments yet. Add one above.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: C.bg }}>
            <tr><th style={th()}>Name</th><th style={{ ...th(), textAlign: 'right' }}></th></tr>
          </thead>
          <tbody>
            {items.map(d => (
              <tr key={d.DepartmentId} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={td()}><strong>{d.Name}</strong></td>
                <td style={{ ...td(), textAlign: 'right' }}>
                  <button onClick={() => setSwModalDept(d)} style={{ ...tinyBtn(C.primary), marginRight: 6 }}>Manage software</button>
                  <button onClick={() => rename(d.DepartmentId, d.Name)} style={{ ...tinyBtn(C.primary), marginRight: 6 }}>Rename</button>
                  <button onClick={() => remove(d.DepartmentId, d.Name)} style={tinyBtn(C.danger)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {swModalDept && (
        <DepartmentSoftwareModal
          dept={swModalDept}
          onClose={() => setSwModalDept(null)}
        />
      )}
    </Card>
  );
};

const UsersTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Currently-open software modal, or null
  const [softwareModalUser, setSoftwareModalUser] = useState<User | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showRetired, setShowRetired] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [data, depts] = await Promise.all([
        api.get<User[]>(`/api/users${showRetired ? '?includeInactive=1' : ''}`),
        api.get<Department[]>('/api/departments').catch(() => [] as Department[]),
      ]);
      setUsers(Array.isArray(data) ? data : []);
      setDepartments(Array.isArray(depts) ? depts : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showRetired]);

  const setDepartment = async (u: User, departmentId: number | null) => {
    try {
      await api.patch(`/api/users/${u.UserId}`, { departmentId });
      await load();
    } catch (e: any) { alert(`Failed to set department: ${e?.message}`); }
  };

  const renameUser = async (u: User) => {
    const name = window.prompt(`Display name for "${u.DisplayName}":\n\n(Handy for labeling service/automation accounts, e.g. "Automation — Email Intake".)`, u.DisplayName);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === u.DisplayName) return;
    try {
      await api.patch(`/api/users/${u.UserId}`, { displayName: trimmed });
      await load();
    } catch (e: any) { alert(`Rename failed: ${e?.message}`); }
  };

  const updateRole = async (id: number, role: string) => {
    try {
      await api.patch(`/api/users/${id}`, { role });
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const deleteUser = async (u: User) => {
    if (!window.confirm(`Permanently delete "${u.DisplayName}"?\n\nThe account is removed for good and CANNOT be restored. Their tickets, comments, and history are kept but relabeled as "Former User".\n\nTo just remove their access while keeping their name on record, use Retire instead.`)) return;
    try {
      await api.del(`/api/users/${u.UserId}`);
      await load();
    } catch (e: any) {
      alert(`Delete failed: ${e?.message ?? e}`);
    }
  };

  const deactivateUser = async (u: User) => {
    try {
      await api.post(`/api/users/${u.UserId}/deactivate`, {});
      await load();
    } catch (e: any) { alert(`Deactivate failed: ${e?.message}`); }
  };

  const reactivateUser = async (u: User) => {
    try {
      await api.post(`/api/users/${u.UserId}/reactivate`, {});
      await load();
    } catch (e: any) { alert(`Reactivate failed: ${e?.message}`); }
  };

  const toggleCanCreate = async (u: User) => {
    const next = !(u.CanCreateTickets ?? true);
    const msg = next
      ? `Allow ${u.DisplayName} to create tickets again?`
      : `Block ${u.DisplayName} from creating new tickets? Existing tickets are unaffected.`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/api/users/${u.UserId}/can-create`, { canCreate: next });
      await load();
    } catch (e: any) { alert(`Update failed: ${e?.message}`); }
  };

  if (loading) return <Card>Loading…</Card>;
  if (error) return <Card><span style={{ color: C.danger }}>Error: {error}</span></Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        Role changes take effect on the user's next API call. Roles synced from Entra app roles override these on next sign-in.
      </p>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} />
        Show retired users
      </label>

      <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ background: C.bg }}>
          <tr>
            <th style={th()}>Name</th>
            <th style={th()}>Email</th>
            <th style={th()}>Role</th>
            <th style={th()}>Department</th>
            <th style={th()}>Status</th>
            <th style={th()}>Can Create</th>
            <th style={th()}>Software</th>
            <th style={th()}></th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const active = u.Active !== false;
            return (
              <tr key={u.UserId} style={{
                borderTop: `1px solid ${C.border}`,
                opacity: active ? 1 : 0.55,
              }}>
                <td style={td()}><strong>{u.DisplayName}</strong></td>
                <td style={{ ...td(), color: C.textMuted, fontFamily: 'monospace', fontSize: 12 }}>{u.Email}</td>
                <td style={td()}>
                  {isAdmin ? (
                    <Select value={u.Role} onChange={(e: any) => updateRole(u.UserId, e.target.value)} style={{ width: 130 }}>
                      {['admin', 'tech', 'user', 'viewer'].map(r => <option key={r} value={r}>{r}</option>)}
                    </Select>
                  ) : (
                    <span style={{ fontSize: 13 }}>{u.Role}</span>
                  )}
                </td>
                <td style={td()}>
                  {isAdmin ? (
                    <Select
                      value={u.DepartmentId ?? ''}
                      onChange={(e: any) => setDepartment(u, e.target.value ? Number(e.target.value) : null)}
                      style={{ width: 150 }}
                    >
                      <option value="">— none —</option>
                      {departments.map(d => <option key={d.DepartmentId} value={d.DepartmentId}>{d.Name}</option>)}
                    </Select>
                  ) : (
                    <span style={{ fontSize: 13 }}>{departments.find(d => d.DepartmentId === u.DepartmentId)?.Name ?? '—'}</span>
                  )}
                </td>
                <td style={td()}>
                  {active ? (
                    <span style={{ fontSize: 11, color: C.ok, fontWeight: 500 }}>Active</span>
                  ) : (
                    <span style={{ fontSize: 11, color: C.textMuted, fontWeight: 500 }}>Retired</span>
                  )}
                </td>
                <td style={td()}>
                  {!isAdmin ? (
                    <span style={{ fontSize: 11, color: (u.CanCreateTickets ?? true) ? C.ok : C.danger, fontWeight: 500 }}>
                      {(u.CanCreateTickets ?? true) ? 'Allowed' : 'Blocked'}
                    </span>
                  ) : (u.CanCreateTickets ?? true) ? (
                    <button
                      onClick={() => toggleCanCreate(u)}
                      style={{
                        background: 'transparent', color: C.ok, fontSize: 11,
                        border: `1px solid ${C.border}`, borderRadius: 4,
                        padding: '4px 10px', cursor: 'pointer', fontWeight: 500,
                      }}
                      title="Currently allowed. Click to block."
                    >Allowed</button>
                  ) : (
                    <button
                      onClick={() => toggleCanCreate(u)}
                      style={{
                        background: '#fdf2e6', color: C.danger, fontSize: 11,
                        border: `1px solid ${C.danger}`, borderRadius: 4,
                        padding: '4px 10px', cursor: 'pointer', fontWeight: 600,
                      }}
                      title="Currently blocked. Click to allow."
                    >Blocked</button>
                  )}
                </td>
                <td style={td()}>
                  <button
                    onClick={() => setSoftwareModalUser(u)}
                    style={{
                      background: 'transparent', color: C.primary,
                      border: `1px solid ${C.border}`,
                      borderRadius: 4, padding: '4px 10px', cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >Manage</button>
                </td>
                <td style={{ ...td(), textAlign: 'right' }}>
                  {active ? (
                    <>
                      <button onClick={() => renameUser(u)} style={{ ...tinyBtn(C.primary), marginRight: 6 }}>Rename</button>
                      {isAdmin && <button onClick={() => deleteUser(u)} style={tinyBtn(C.danger)}>Delete</button>}
                      <button onClick={() => deactivateUser(u)} style={{ ...tinyBtn(C.textMuted), marginLeft: 6 }}>Retire</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => renameUser(u)} style={{ ...tinyBtn(C.primary), marginRight: 6 }}>Rename</button>
                      <button onClick={() => reactivateUser(u)} style={tinyBtn(C.ok)}>Reactivate</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>

      {softwareModalUser && (
        <UserSoftwareModal
          user={softwareModalUser}
          onClose={() => setSoftwareModalUser(null)}
        />
      )}
    </Card>
  );
};

const tinyBtn = (color: string): React.CSSProperties => ({
  background: 'transparent', border: `1px solid ${color}40`,
  color, padding: '4px 10px', borderRadius: 4, fontSize: 12,
  cursor: 'pointer', fontWeight: 500,
});

// ============================================================================
// User Software Modal — manage a single user's approved software list
// ============================================================================
interface SoftwareEntry {
  SoftwareId: number;
  Name: string;
  Notes: string | null;
  AddedAt: string;
  AddedByName?: string | null;
}

const UserSoftwareModal: React.FC<{
  user: User;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const api = useApi();
  const [items, setItems] = useState<SoftwareEntry[]>([]);
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Add mode: 'pick' from catalog or 'bulk' paste comma-separated
  const [addMode, setAddMode] = useState<'pick' | 'bulk'>('pick');
  // Pick mode
  const [pickedCatalogId, setPickedCatalogId] = useState<number | ''>('');
  const [customName, setCustomName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  // Bulk mode
  const [bulkText, setBulkText] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sw, cat] = await Promise.all([
        api.get<SoftwareEntry[]>(`/api/users/${user.UserId}/software`),
        api.get<CatalogRow[]>('/api/users/software-catalog').catch(() => [] as CatalogRow[]),
      ]);
      setItems(Array.isArray(sw) ? sw : []);
      setCatalog(Array.isArray(cat) ? cat : []);
    } catch (e: any) {
      alert(`Failed to load software: ${e?.message}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.UserId]);

  // Group catalog entries by category for the select
  const catalogByCategory: Record<string, CatalogRow[]> = {};
  for (const it of catalog) {
    const k = it.Category || 'Uncategorized';
    if (!catalogByCategory[k]) catalogByCategory[k] = [];
    catalogByCategory[k].push(it);
  }
  const catKeys = Object.keys(catalogByCategory).sort();

  const addFromPicker = async () => {
    let name = customName.trim();
    if (!name && pickedCatalogId !== '') {
      const picked = catalog.find(c => c.SoftwareCatalogId === pickedCatalogId);
      if (picked) name = picked.Name;
    }
    if (!name) return;
    setAdding(true);
    try {
      await api.post(`/api/users/${user.UserId}/software`, {
        name,
        notes: newNotes.trim() || undefined,
      });
      setPickedCatalogId(''); setCustomName(''); setNewNotes('');
      await load();
    } catch (e: any) { alert(`Add failed: ${e?.message}`); }
    finally { setAdding(false); }
  };

  const addBulk = async () => {
    const text = bulkText.trim();
    if (!text) return;
    setAdding(true);
    try {
      const result = await api.post<{ added: number; skipped: string[] }>(
        `/api/users/${user.UserId}/software/bulk`,
        { names: text }
      );
      setBulkText('');
      await load();
      const skippedNote = result.skipped.length > 0 ? `\n\nAlready had: ${result.skipped.join(', ')}` : '';
      alert(`Added ${result.added} software entries.${skippedNote}`);
    } catch (e: any) { alert(`Bulk add failed: ${e?.message}`); }
    finally { setAdding(false); }
  };

  const removeItem = async (entry: SoftwareEntry) => {
    if (!window.confirm(`Remove "${entry.Name}" from ${user.DisplayName}'s software list?`)) return;
    try {
      await api.del(`/api/users/${user.UserId}/software/${entry.SoftwareId}`);
      await load();
    } catch (e: any) { alert(`Remove failed: ${e?.message}`); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.surface, borderRadius: 8, width: '100%', maxWidth: 720,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>
              {user.DisplayName} — Approved Software
            </h3>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
              {user.Email}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.textMuted, fontSize: 18, padding: 4,
          }}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {/* Add new — tabbed picker / bulk */}
          <div style={{
            background: C.bg, padding: 14, borderRadius: 6,
            border: `1px solid ${C.border}`, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              <button
                onClick={() => setAddMode('pick')}
                style={addModeTab(addMode === 'pick')}
              >Pick from catalog</button>
              <button
                onClick={() => setAddMode('bulk')}
                style={addModeTab(addMode === 'bulk')}
              >Bulk add (comma-separated)</button>
            </div>

            {addMode === 'pick' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                  <select
                    value={pickedCatalogId}
                    onChange={e => {
                      setPickedCatalogId(e.target.value ? parseInt(e.target.value) : '');
                      setCustomName('');  // clear custom when picking from catalog
                    }}
                    style={inputSty()}
                  >
                    <option value="">— Pick from catalog —</option>
                    {catKeys.map(cat => (
                      <optgroup key={cat} label={cat}>
                        {catalogByCategory[cat].map(it => (
                          <option key={it.SoftwareCatalogId} value={it.SoftwareCatalogId}>
                            {it.Name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <input
                    value={customName}
                    onChange={e => { setCustomName(e.target.value); if (e.target.value) setPickedCatalogId(''); }}
                    placeholder="Or type custom name"
                    style={inputSty()}
                  />
                  <button
                    onClick={addFromPicker}
                    disabled={(!customName.trim() && pickedCatalogId === '') || adding}
                    style={{
                      background: (customName.trim() || pickedCatalogId !== '') ? C.primary : '#ccc',
                      color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px',
                      fontSize: 12, fontWeight: 600,
                      cursor: (customName.trim() || pickedCatalogId !== '') ? 'pointer' : 'not-allowed',
                    }}
                  >{adding ? '…' : 'Add'}</button>
                </div>
                <input
                  value={newNotes}
                  onChange={e => setNewNotes(e.target.value)}
                  placeholder="Notes / license / version (optional)"
                  style={{ ...inputSty(), marginTop: 8 }}
                />
              </>
            )}

            {addMode === 'bulk' && (
              <>
                <textarea
                  value={bulkText}
                  onChange={e => setBulkText(e.target.value)}
                  placeholder="Paste a comma-separated list, e.g.: Excel, Word, Photoshop, Slack, Zoom"
                  rows={3}
                  style={{ ...inputSty(), resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: C.textMuted }}>
                    Duplicates are skipped. Up to 200 entries at once.
                  </span>
                  <button
                    onClick={addBulk}
                    disabled={!bulkText.trim() || adding}
                    style={{
                      background: !bulkText.trim() ? '#ccc' : C.primary, color: '#fff',
                      border: 'none', borderRadius: 4, padding: '6px 16px',
                      fontSize: 12, fontWeight: 600,
                      cursor: !bulkText.trim() ? 'not-allowed' : 'pointer',
                    }}
                  >{adding ? 'Adding…' : 'Add all'}</button>
                </div>
              </>
            )}
          </div>

          {/* List */}
          {loading ? (
            <div style={{ color: C.textMuted, fontSize: 13 }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{
              color: C.textMuted, fontSize: 13, padding: 32, textAlign: 'center',
              border: `1px dashed ${C.border}`, borderRadius: 6,
            }}>
              No software tracked yet. Add what they're approved for above.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: C.bg }}>
                <tr>
                  <th style={th()}>Software</th>
                  <th style={th()}>Notes</th>
                  <th style={th()}>Added</th>
                  <th style={th()}></th>
                </tr>
              </thead>
              <tbody>
                {items.map(e => (
                  <tr key={e.SoftwareId} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={td()}><strong>{e.Name}</strong></td>
                    <td style={{ ...td(), color: C.textMuted }}>{e.Notes || '—'}</td>
                    <td style={{ ...td(), color: C.textMuted, fontSize: 11 }}>
                      {new Date(e.AddedAt).toLocaleDateString()}
                      {e.AddedByName && <><br /><span style={{ fontSize: 10 }}>by {e.AddedByName}</span></>}
                    </td>
                    <td style={{ ...td(), textAlign: 'right' }}>
                      <button
                        onClick={() => removeItem(e)}
                        style={tinyBtn(C.danger)}
                      >Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>

        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            padding: '6px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
          }}>Done</button>
        </div>
      </div>
    </div>
  );
};

const addModeTab = (active: boolean): React.CSSProperties => ({
  background: active ? C.primary : 'transparent',
  color: active ? '#fff' : C.text,
  border: `1px solid ${active ? C.primary : C.border}`,
  padding: '5px 12px', borderRadius: 4, fontSize: 12,
  cursor: 'pointer', fontWeight: active ? 600 : 400,
});


// -------- Style helpers --------
const th = (): React.CSSProperties => ({
  padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px',
});
const td = (): React.CSSProperties => ({ padding: '10px 12px', verticalAlign: 'middle' });
const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '6px 10px', borderRadius: 6, fontSize: 13, background: C.bg,
});
const lblSty = (): React.CSSProperties => ({
  display: 'block', fontSize: 11, color: C.textMuted, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6,
});

// ============================================================================
// Business Hours Tab — schedule + timezone + critical bypass + SLA stop-statuses
// ============================================================================
interface BHDay {
  DayOfWeek: number;
  Enabled: boolean;
  StartMinutes: number | null;
  EndMinutes: number | null;
}

const BusinessHoursTab: React.FC = () => {
  const api = useApi();
  const [days, setDays] = useState<BHDay[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [timezone, setTimezone] = useState('America/New_York');
  const [criticalAlwaysCounts, setCriticalAlwaysCounts] = useState(true);
  const [stopStatusIds, setStopStatusIds] = useState<number[]>([]);
  const [allStatuses, setAllStatuses] = useState<{ StatusId: number; Name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [bh, stop, st] = await Promise.all([
        api.get<{ days: BHDay[]; enabled: boolean; timezone: string; criticalAlwaysCounts: boolean }>('/api/admin/business-hours'),
        api.get<{ stopStatusIds: number[] }>('/api/admin/sla-stop-statuses'),
        api.get<{ StatusId: number; Name: string }[]>('/api/admin/statuses'),
      ]);
      setDays(bh.days);
      setEnabled(bh.enabled);
      setTimezone(bh.timezone);
      setCriticalAlwaysCounts(bh.criticalAlwaysCounts);
      setStopStatusIds(stop.stopStatusIds);
      setAllStatuses(Array.isArray(st) ? st : []);
    } catch (e: any) {
      alert(`Failed to load business hours: ${e?.message}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/api/admin/business-hours', {
        enabled,
        timezone,
        criticalAlwaysCounts,
        days: days.map(d => ({
          dayOfWeek: d.DayOfWeek,
          enabled: d.Enabled,
          startMinutes: d.StartMinutes,
          endMinutes: d.EndMinutes,
        })),
      });
      await api.patch('/api/admin/sla-stop-statuses', { stopStatusIds });
      alert('Business hours saved.');
    } catch (e: any) { alert(`Save failed: ${e?.message}`); }
    finally { setSaving(false); }
  };

  const updateDay = (dow: number, patch: Partial<BHDay>) => {
    setDays(prev => prev.map(d => d.DayOfWeek === dow ? { ...d, ...patch } : d));
  };

  const toggleStopStatus = (statusId: number) => {
    setStopStatusIds(prev => prev.includes(statusId)
      ? prev.filter(x => x !== statusId)
      : [...prev, statusId]);
  };

  const minutesToHHMM = (m: number | null): string => {
    if (m === null) return '';
    const h = Math.floor(m / 60).toString().padStart(2, '0');
    const mm = (m % 60).toString().padStart(2, '0');
    return `${h}:${mm}`;
  };
  const hhmmToMinutes = (s: string): number | null => {
    if (!s) return null;
    const [h, m] = s.split(':').map(x => parseInt(x));
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // A short list of common timezones; the input is also editable so users can type any IANA tz
  const commonTzs = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'America/Honolulu',
    'Europe/London', 'Europe/Paris', 'UTC',
  ];

  if (loading) return <Card>Loading…</Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        Configure when SLA timers actively count. Outside these hours, the clock pauses.
        Critical-priority tickets can be configured to ignore business hours entirely.
      </p>

      {/* Enable / timezone / critical-bypass */}
      <div style={{ marginBottom: 24, padding: 14, background: C.bg, borderRadius: 6, border: `1px solid ${C.border}` }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          <strong style={{ fontSize: 13 }}>Use business hours for SLA timing</strong>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={criticalAlwaysCounts} onChange={e => setCriticalAlwaysCounts(e.target.checked)} disabled={!enabled} />
          <span style={{ fontSize: 13 }}>Critical priority bypasses business hours (always counts 24/7)</span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13 }}>Timezone:</span>
          <input
            list="tz-list"
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            disabled={!enabled}
            style={{ ...inputSty(), width: 220 }}
          />
          <datalist id="tz-list">
            {commonTzs.map(tz => <option key={tz} value={tz} />)}
          </datalist>
        </div>
      </div>

      {/* Weekly schedule */}
      <h4 style={{ margin: '0 0 8px', fontSize: 13, color: C.text }}>Weekly schedule</h4>
      <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13, marginBottom: 24 }}>
        <thead style={{ background: C.bg }}>
          <tr>
            <th style={th()}>Day</th>
            <th style={th()}>Open</th>
            <th style={th()}>Start</th>
            <th style={th()}>End</th>
          </tr>
        </thead>
        <tbody>
          {days.map(d => (
            <tr key={d.DayOfWeek} style={{ borderTop: `1px solid ${C.border}`, opacity: enabled ? 1 : 0.5 }}>
              <td style={td()}><strong>{dayLabels[d.DayOfWeek]}</strong></td>
              <td style={td()}>
                <input
                  type="checkbox"
                  checked={d.Enabled}
                  disabled={!enabled}
                  onChange={e => updateDay(d.DayOfWeek, { Enabled: e.target.checked })}
                />
              </td>
              <td style={td()}>
                <input
                  type="time"
                  value={minutesToHHMM(d.StartMinutes)}
                  disabled={!enabled || !d.Enabled}
                  onChange={e => updateDay(d.DayOfWeek, { StartMinutes: hhmmToMinutes(e.target.value) })}
                  style={{ ...inputSty(), width: 110 }}
                />
              </td>
              <td style={td()}>
                <input
                  type="time"
                  value={minutesToHHMM(d.EndMinutes)}
                  disabled={!enabled || !d.Enabled}
                  onChange={e => updateDay(d.DayOfWeek, { EndMinutes: hhmmToMinutes(e.target.value) })}
                  style={{ ...inputSty(), width: 110 }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {/* SLA stop-statuses */}
      <h4 style={{ margin: '0 0 4px', fontSize: 13, color: C.text }}>Statuses that pause SLA</h4>
      <p style={{ fontSize: 12, color: C.textMuted, margin: '0 0 8px' }}>
        Tickets in these statuses don't count against the SLA clock. Useful for "waiting on customer" etc.
      </p>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6,
        marginBottom: 24, padding: 12, background: C.bg, borderRadius: 6, border: `1px solid ${C.border}`,
      }}>
        {allStatuses.map(s => (
          <label
            key={s.StatusId}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
              padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
              background: stopStatusIds.includes(s.StatusId) ? '#fff' : 'transparent',
            }}
          >
            <input
              type="checkbox"
              checked={stopStatusIds.includes(s.StatusId)}
              onChange={() => toggleStopStatus(s.StatusId)}
            />
            {s.Name}
          </label>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        style={{
          background: C.primary, color: 'white', border: 'none',
          padding: '8px 20px', borderRadius: 4, fontSize: 13, fontWeight: 600,
          cursor: 'pointer',
        }}
      >{saving ? 'Saving…' : 'Save business hours'}</button>
    </Card>
  );
};

// ============================================================================
// Categories Tab — add / rename / delete / reorder ticket categories
// ============================================================================
const CategoriesTab: React.FC = () => {
  const api = useApi();
  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<Category[]>('/api/categories');
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) { alert(`Failed to load categories: ${e?.message}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post('/api/categories', { name, sortOrder: items.length + 1 });
      setNewName('');
      await load();
    } catch (e: any) { alert(`Add failed: ${e?.message}`); }
    finally { setSaving(false); }
  };

  const rename = async (id: number, oldName: string) => {
    const v = window.prompt('New category name:', oldName);
    if (!v || v.trim() === oldName) return;
    try {
      await api.patch(`/api/categories/${id}`, { name: v.trim() });
      await load();
    } catch (e: any) { alert(`Rename failed: ${e?.message}`); }
  };

  const remove = async (id: number, name: string) => {
    if (!window.confirm(
      `Delete category "${name}"?\n\nTickets in this category will be set to "Uncategorized".`
    )) return;
    try {
      await api.del(`/api/categories/${id}`);
      await load();
    } catch (e: any) { alert(`Delete failed: ${e?.message}`); }
  };

  if (loading) return <Card>Loading…</Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
        Categories help group tickets — used by routing rules and reports. Anyone can pick one when filing a ticket.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="New category name (e.g. Hardware, Software, Access)"
          style={{
            flex: 1, padding: '8px 12px', fontSize: 13,
            border: `1px solid ${C.border}`, borderRadius: 4, background: C.surface,
          }}
        />
        <button
          onClick={add}
          disabled={!newName.trim() || saving}
          style={{
            background: !newName.trim() ? '#ccc' : C.primary, color: 'white',
            border: 'none', padding: '8px 18px', borderRadius: 4,
            fontSize: 13, fontWeight: 600,
            cursor: !newName.trim() ? 'not-allowed' : 'pointer',
          }}
        >Add category</button>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
          No categories yet. Add one above.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: C.bg }}>
            <tr><th style={th()}>Name</th><th style={{ ...th(), textAlign: 'right' }}></th></tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.CategoryId} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={td()}>{c.Name}</td>
                <td style={{ ...td(), textAlign: 'right' }}>
                  <button onClick={() => rename(c.CategoryId, c.Name)} style={tinyBtn(C.primary)}>Rename</button>
                  <button onClick={() => remove(c.CategoryId, c.Name)} style={{ ...tinyBtn(C.danger), marginLeft: 6 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
};

// ============================================================================
// Software Catalog Tab — list of pickable software entries, grouped by category.
// Used by the per-user "Add software" modal to pull from a shared list.
// ============================================================================
interface CatalogRow {
  SoftwareCatalogId: number;
  Name: string;
  Category: string | null;
  SortOrder: number;
}
// -------- Audit Log Tab (admin only) --------
const AUDIT_LABELS: Record<string, string> = {
  status_changed: 'Status changed',
  assignee_changed: 'Assignee changed',
  priority_changed: 'Priority changed',
  title_changed: 'Title changed',
  tickets_merged: 'Tickets merged',
  asset_linked: 'Device linked',
  asset_unlinked: 'Device unlinked',
  comment_edited: 'Comment edited',
  comment_deleted: 'Comment deleted',
};

interface AuditRow {
  AuditId: number;
  TicketId: number;
  TicketKey: string | null;
  Action: string;
  Detail: string | null;
  CreatedAt: string;
  ActorName: string | null;
}

const AuditLogTab: React.FC = () => {
  const api = useApi();
  const PAGE_SIZE = 50;
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [ticket, setTicket] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (ticket.trim()) qs.set('ticket', ticket.trim());
      if (action) qs.set('action', action);
      qs.set('page', String(page));
      qs.set('pageSize', String(PAGE_SIZE));
      const data = await api.get<{ audit: AuditRow[]; actions: string[]; total: number }>(
        `/api/admin/audit?${qs.toString()}`
      );
      setRows(Array.isArray(data.audit) ? data.audit : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      if (Array.isArray(data.actions)) setActions(data.actions);
      setError(null);
    } catch (e: any) { setError(e?.message ?? 'Failed to load'); }
    finally { setLoading(false); }
  };
  // Debounced reload when filters or page change
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [ticket, action, page]);

  // Changing a filter snaps back to page 1
  const onTicket = (v: string) => { setTicket(v); setPage(1); };
  const onAction = (v: string) => { setAction(v); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = (page - 1) * PAGE_SIZE + rows.length;

  const inputSty: React.CSSProperties = {
    border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px',
    fontSize: 13, background: C.surface, color: C.text,
  };
  const th: React.CSSProperties = {
    textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
    letterSpacing: '0.4px', color: C.textMuted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { padding: '9px 10px', fontSize: 13, borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' };

  return (
    <Card>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Filter by ticket (e.g. TK-1115 or 1115)"
          value={ticket}
          onChange={e => onTicket(e.target.value)}
          style={{ ...inputSty, flex: 1, minWidth: 220 }}
        />
        <select value={action} onChange={e => onAction(e.target.value)} style={{ ...inputSty, minWidth: 180 }}>
          <option value="">All actions</option>
          {actions.map(a => <option key={a} value={a}>{AUDIT_LABELS[a] ?? a}</option>)}
        </select>
        {(ticket || action) && (
          <button onClick={() => { setTicket(''); setAction(''); setPage(1); }}
            style={{ background: 'transparent', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </div>

      {error ? (
        <div style={{ color: C.danger, fontSize: 13, padding: 12 }}>Failed to load: {error}</div>
      ) : loading ? (
        <div style={{ color: C.textMuted, fontSize: 13, padding: 12 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' }}>
          No audit entries{ticket || action ? ' match the filter' : ' yet'}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>When</th>
                <th style={th}>Ticket</th>
                <th style={th}>Who</th>
                <th style={th}>Action</th>
                <th style={th}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.AuditId}>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: C.textMuted }}>{new Date(r.CreatedAt).toLocaleString()}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{r.TicketKey ?? `#${r.TicketId}`}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.ActorName ?? '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{AUDIT_LABELS[r.Action] ?? r.Action}</td>
                  <td style={td}>{r.Detail ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: C.textMuted }}>
              Showing {startIdx}–{endIdx} of {total}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                style={{
                  background: 'transparent', color: page <= 1 ? C.textMuted : C.primary,
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px',
                  fontSize: 13, cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
                }}>
                ‹ Prev
              </button>
              <span style={{ fontSize: 12, color: C.textMuted }}>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages || loading}
                style={{
                  background: 'transparent', color: page >= totalPages ? C.textMuted : C.primary,
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px',
                  fontSize: 13, cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.5 : 1,
                }}>
                Next ›
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
